// Demo-Datenquelle: hält den kompletten Stand im Browser und wendet dieselben
// Regeln an wie die Datenbank (inkl. Absage-Automatik). Speichert in localStorage.
import seed from '../demo/seed.json';
import type { Stand, Id, EinsatzEntwurf, Einsatz, Abwesenheit, Absage, KursKnoten } from './typen';
import { type Datenquelle, type Tabelle, type NeuerKurs, type AbwesenheitEingabe, RegelFehler, TABELLE_ZU_STAND } from './quelle';
import { pruefeEinsatz, kinderVon } from './regeln';
import { zeitpunkt, ueberschneidet, heute } from './zeit';

const SCHLUESSEL = 'mm-planung-demo-v2';
const neueId = () => (globalThis.crypto?.randomUUID?.() ?? `id-${Math.random().toString(36).slice(2)}-${Date.now()}`);

function ausSeed(): Stand {
  const s = seed as unknown as Record<string, any[]>;
  return {
    mandant: s.mandant[0], dozenten: s.dozent, fachbereiche: s.fachbereich, qualifikationen: s.dozent_qualifikation,
    vorlagen: s.lehrgang_vorlage, vorlageKnoten: s.vorlage_knoten, standorte: s.standort, raeume: s.raum,
    zeitraster: s.zeitraster, zeitfenster: s.zeitfenster, kalender: s.kalender_tag, kurse: s.kurs, kursKnoten: s.kurs_knoten,
    abwesenheiten: s.abwesenheit, einsaetze: s.einsatz, absagen: s.absage, appNutzer: (s as any).app_nutzer ?? [],
  } as Stand;
}
const kopie = <T,>(x: T): T => JSON.parse(JSON.stringify(x));

// Ein gemeinsamer Datenstand für alle Ansichten dieser Seite (Verwaltung und Dozenten-App)
let stand: Stand;
try {
  const roh = localStorage.getItem(SCHLUESSEL);
  stand = roh ? JSON.parse(roh) : ausSeed();
} catch { stand = ausSeed(); }
stand.appNutzer ??= [];
const hoerer = new Set<() => void>();
const sichern = () => {
  try { localStorage.setItem(SCHLUESSEL, JSON.stringify(stand)); } catch { /* privater Modus o. Ä. */ }
  hoerer.forEach(h => h());
};
// Änderungen aus anderen Tabs desselben Browsers übernehmen (simuliert Echtzeit)
try {
  window.addEventListener('storage', ev => {
    if (ev.key === SCHLUESSEL && ev.newValue) { stand = JSON.parse(ev.newValue); hoerer.forEach(h => h()); }
  });
} catch { /* ohne window */ }

/** Sicht eines Dozenten auf die Daten – bildet die Zugriffsrechte der Datenbank nach */
function dozentenSicht(s: Stand, dozentId: Id): Stand {
  return {
    ...s,
    dozenten: s.dozenten.filter(d => d.id === dozentId),
    qualifikationen: s.qualifikationen.filter(q => q.dozent_id === dozentId),
    einsaetze: s.einsaetze.filter(e => e.dozent_id === dozentId && e.status === 'freigegeben'),
    abwesenheiten: s.abwesenheiten.filter(a => a.dozent_id === dozentId),
    absagen: s.absagen.filter(a => a.dozent_id === dozentId),
    vorlagen: [], vorlageKnoten: [], appNutzer: [dozentId],
  };
}

export function demoQuelle(rolle: 'admin' | 'dozent' = 'admin', dozentId?: Id): Datenquelle {

  const absagenErledigen = (einsatzId: Id) => {
    for (const a of stand.absagen) if (a.einsatz_id === einsatzId && !a.erledigt_am) a.erledigt_am = new Date().toISOString();
  };
  const konflikteAufloesen = (abw: Abwesenheit) => {
    for (const e of stand.einsaetze) {
      if (e.dozent_id !== abw.dozent_id || e.status === 'entfaellt') continue;
      if (!ueberschneidet(abw.von, abw.bis, zeitpunkt(e.datum, e.beginn), zeitpunkt(e.datum, e.ende))) continue;
      const grund = abw.quelle === 'absage' ? (abw.grund ?? 'Absage durch Dozent')
        : abw.quelle === 'admin' ? `Abwesenheit (durch Verwaltung eingetragen)${abw.grund ? ': ' + abw.grund : ''}`
        : `Abwesenheit eingetragen${abw.grund ? ': ' + abw.grund : ''}`;
      const absage: Absage = { id: neueId(), mandant_id: e.mandant_id, einsatz_id: e.id, dozent_id: abw.dozent_id, grund, erstellt_am: new Date().toISOString(), erledigt_am: null };
      stand.absagen.push(absage);
      e.dozent_id = null;
    }
  };
  const einsatzEinfuegen = (e: EinsatzEntwurf): Einsatz => {
    const v = pruefeEinsatz(stand, e);
    if (v) throw new RegelFehler(v);
    const alt = e.id ? stand.einsaetze.find(x => x.id === e.id) : undefined;
    if (alt) {
      const hatteKeinen = !alt.dozent_id;
      if (e.status === 'freigegeben' && (alt.status !== 'freigegeben' || alt.dozent_id !== e.dozent_id)) e = { ...e, freigegeben_am: new Date().toISOString() };
      Object.assign(alt, e);
      if ((hatteKeinen && alt.dozent_id) || alt.status === 'entfaellt') absagenErledigen(alt.id);
      return alt;
    }
    const neu: Einsatz = { ...e, id: neueId(), mandant_id: stand.mandant.id, freigegeben_am: e.status === 'freigegeben' ? new Date().toISOString() : null } as Einsatz;
    stand.einsaetze.push(neu);
    return neu;
  };

  const nurAdmin = () => { if (rolle !== 'admin') throw new RegelFehler({ code: 'RLS' }); };
  const q: Datenquelle = {
    art: 'demo', rolle,
    async laden() { return kopie(rolle === 'dozent' && dozentId ? dozentenSicht(stand, dozentId) : stand); },
    abonnieren(cb) { hoerer.add(cb); return () => hoerer.delete(cb); },

    async einsatzSpeichern(e) { nurAdmin(); einsatzEinfuegen(e); sichern(); },
    async einsaetzeAnlegen(liste) {
      let n = 0;
      for (const e of liste) { try { einsatzEinfuegen({ ...e, id: undefined }); n++; } catch { /* übersprungen */ } }
      sichern(); return n;
    },
    async einsatzLoeschen(id) {
      stand.einsaetze = stand.einsaetze.filter(e => e.id !== id);
      stand.absagen = stand.absagen.filter(a => a.einsatz_id !== id);
      sichern();
    },
    async freigeben(ids) {
      nurAdmin();
      const set = new Set(ids);
      const jetzt = new Date().toISOString();
      for (const e of stand.einsaetze) if (set.has(e.id) && e.status === 'entwurf') { e.status = 'freigegeben'; e.freigegeben_am = jetzt; }
      sichern();
    },

    async abwesenheitSpeichern(a: AbwesenheitEingabe) {
      if (a.bis <= a.von) throw new RegelFehler({ code: 'ZEIT_UNGUELTIG' });
      if (rolle === 'dozent' && (a.dozent_id !== dozentId || a.quelle !== 'dozent')) throw new RegelFehler({ code: 'RLS' });
      let ziel: Abwesenheit;
      const alt = a.id ? stand.abwesenheiten.find(x => x.id === a.id) : undefined;
      if (alt) { Object.assign(alt, a); ziel = alt; }
      else { ziel = { ...a, id: neueId(), mandant_id: stand.mandant.id } as Abwesenheit; stand.abwesenheiten.push(ziel); }
      konflikteAufloesen(ziel);
      sichern();
    },
    async abwesenheitLoeschen(id) { stand.abwesenheiten = stand.abwesenheiten.filter(a => a.id !== id); sichern(); },
    async einsatzAbsagen(id, grund) {
      const e = stand.einsaetze.find(x => x.id === id);
      if (!e || !e.dozent_id || (rolle === 'dozent' && e.dozent_id !== dozentId)) throw new RegelFehler({ code: 'KEIN_EIGENER_EINSATZ' });
      if (e.status !== 'freigegeben') throw new RegelFehler({ code: 'EINSATZ_NICHT_FREIGEGEBEN' });
      if (zeitpunkt(e.datum, e.ende) <= zeitpunkt(heute(), new Date().toTimeString().slice(0, 5))) throw new RegelFehler({ code: 'EINSATZ_VERGANGEN' });
      const abw: Abwesenheit = { id: neueId(), mandant_id: e.mandant_id, dozent_id: e.dozent_id, von: zeitpunkt(e.datum, e.beginn),
        bis: zeitpunkt(e.datum, e.ende), grund: grund?.trim() || 'Absage durch Dozent', quelle: 'absage' };
      stand.abwesenheiten.push(abw);
      konflikteAufloesen(abw);
      sichern();
    },
    async dozentEinladen(id) {
      nurAdmin();
      const d = stand.dozenten.find(x => x.id === id);
      if (!d?.email) throw new RegelFehler({ code: 'UNBEKANNT', detail: 'Für die Einladung fehlt eine E-Mail-Adresse' });
      if (!stand.appNutzer.includes(id)) stand.appNutzer.push(id);
      sichern();
    },
    async absageErledigen(id) {
      const a = stand.absagen.find(x => x.id === id); if (a) a.erledigt_am = new Date().toISOString(); sichern();
    },

    async kursAnlegen(k: NeuerKurs) {
      if (stand.kurse.some(x => x.kennung === k.kennung)) throw new RegelFehler({ code: 'UNBEKANNT', detail: 'Kennung bereits vergeben' });
      const id = neueId();
      stand.kurse.push({ id, mandant_id: stand.mandant.id, status: 'planung', farbe: k.farbe ?? '#073070', moodle_token: neueId(), ...k });
      const vk = stand.vorlageKnoten.filter(v => v.vorlage_id === k.vorlage_id);
      const map = new Map(vk.map(v => [v.id, neueId()]));
      for (const v of vk) {
        const neu: KursKnoten = { id: map.get(v.id)!, mandant_id: stand.mandant.id, kurs_id: id, parent_id: v.parent_id ? map.get(v.parent_id)! : null,
          bezeichnung: v.bezeichnung, kuerzel: v.kuerzel, fachbereich_id: v.fachbereich_id, soll_ue: v.soll_ue, sortierung: v.sortierung, standard_dozent_id: null };
        stand.kursKnoten.push(neu);
      }
      sichern(); return id;
    },
    async qualifikationSetzen(dozentId, fachbereichId, an) {
      stand.qualifikationen = stand.qualifikationen.filter(x => !(x.dozent_id === dozentId && x.fachbereich_id === fachbereichId));
      if (an) stand.qualifikationen.push({ mandant_id: stand.mandant.id, dozent_id: dozentId, fachbereich_id: fachbereichId });
      sichern();
    },
    async speichern(tabelle: Tabelle, zeile: any) {
      if (tabelle === 'mandant') { Object.assign(stand.mandant, zeile); sichern(); return stand.mandant.id; }
      const liste = stand[TABELLE_ZU_STAND[tabelle]] as any[];
      const alt = zeile.id ? liste.find(x => x.id === zeile.id) : undefined;
      if (alt) { Object.assign(alt, zeile); sichern(); return alt.id; }
      const neu = { ...zeile, id: zeile.id ?? neueId(), mandant_id: stand.mandant.id };
      liste.push(neu); sichern(); return neu.id;
    },
    async loeschen(tabelle: Tabelle, id) {
      const key = TABELLE_ZU_STAND[tabelle];
      if (tabelle === 'kurs_knoten' || tabelle === 'vorlage_knoten') {
        const liste = stand[key] as KursKnoten[];
        const kinder = kinderVon(liste as KursKnoten[]);
        const weg = new Set<Id>();
        const lauf = (x: Id) => { weg.add(x); for (const c of kinder.get(x) ?? []) lauf(c.id); };
        lauf(id);
        (stand as any)[key] = liste.filter(k => !weg.has(k.id));
        if (tabelle === 'kurs_knoten') for (const e of stand.einsaetze) if (e.kurs_knoten_id && weg.has(e.kurs_knoten_id)) e.kurs_knoten_id = null;
      } else if (tabelle === 'kurs') {
        stand.kurse = stand.kurse.filter(k => k.id !== id);
        stand.kursKnoten = stand.kursKnoten.filter(k => k.kurs_id !== id);
        const eIds = new Set(stand.einsaetze.filter(e => e.kurs_id === id).map(e => e.id));
        stand.einsaetze = stand.einsaetze.filter(e => !eIds.has(e.id));
        stand.absagen = stand.absagen.filter(a => !eIds.has(a.einsatz_id));
      } else {
        (stand as any)[key] = (stand[key] as any[]).filter(x => x.id !== id);
        if (tabelle === 'dozent') {
          stand.qualifikationen = stand.qualifikationen.filter(q => q.dozent_id !== id);
          stand.abwesenheiten = stand.abwesenheiten.filter(a => a.dozent_id !== id);
          for (const e of stand.einsaetze) if (e.dozent_id === id) e.dozent_id = null;
        }
        if (tabelle === 'raum') for (const e of stand.einsaetze) if (e.raum_id === id) e.raum_id = null;
      }
      sichern();
    },
    async zuruecksetzen() { stand = ausSeed(); sichern(); },
  };
  return q;
}
